// Reading the answer's own record of what it did with messages sent mid-work
// (`data-steer`, `data-steerUnread`), and what must hold the unread ones for the
// person when the answer ends (CONTRACT v1 §3, §5).
import { hasAskedQuestions } from '@/workspace/question-contract';
import type { SteerHold } from './store';

type Part = {
  type: string;
  name?: string;
  data?: unknown;
  toolName?: string;
  result?: unknown;
};
export type SteerMessage = {
  role: string;
  status?: { type: string; reason?: string } | undefined;
  content: readonly Part[];
};

export type SteerRead = { id: string; text: string };
export type SteerUnread = {
  reason: 'finished' | 'stopped' | 'error';
  messages: { id: string; text: string }[];
};

function readText(value: unknown): SteerRead | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as { id?: unknown; text?: unknown };
  return typeof data.id === 'string' && typeof data.text === 'string'
    ? { id: data.id, text: data.text }
    : null;
}

/** A `data-steer` part's message, or null for anything else. */
export function steerReadPart(part: Part): SteerRead | null {
  return part.type === 'data' && part.name === 'steer' ? readText(part.data) : null;
}

/** A `data-steerUnread` part's report, or null for anything else. */
export function steerUnreadPart(part: Part): SteerUnread | null {
  if (part.type !== 'data' || part.name !== 'steerUnread') return null;
  const data = part.data as { reason?: unknown; messages?: unknown } | null | undefined;
  const reason =
    data?.reason === 'stopped' || data?.reason === 'error' || data?.reason === 'finished'
      ? data.reason
      : 'finished';
  const messages = Array.isArray(data?.messages)
    ? data.messages.flatMap((value) => {
        const message = readText(value);
        return message ? [message] : [];
      })
    : [];
  return messages.length ? { reason, messages } : null;
}

/** The tool indices of an answer, split where it read a message: [before, after 1st, after 2nd, …]. */
export function splitAtSteers(parts: readonly Part[], toolIndices: readonly number[]): number[][] {
  const steers = parts.flatMap((part, index) => (steerReadPart(part) ? [index] : []));
  const segments: number[][] = [[]];
  let next = 0;
  for (const index of toolIndices) {
    while (next < steers.length && index > steers[next]) {
      segments.push([]);
      next++;
    }
    segments[segments.length - 1].push(index);
  }
  while (segments.length < steers.length + 1) segments.push([]);
  return segments;
}

/**
 * Whether the unread messages of an answer that just ended must wait for the
 * person, and why. A message written before a question or an approval card is
 * never sent as its answer; a stopped or failed answer never sends by itself.
 */
export function steerHoldFor(
  last: SteerMessage | undefined,
  pendingApprovals: number,
): SteerHold | null {
  if (pendingApprovals > 0) return 'approval';
  if (!last || last.role !== 'assistant') return null;
  const status = last.status;
  if (status?.type === 'incomplete') return status.reason === 'cancelled' ? 'stopped' : 'failed';
  if (status?.type === 'requires-action') return 'question';
  if (last.content.some(hasAskedQuestions)) return 'question';
  return null;
}
